import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export context for direct access when needed
export { AuthContext };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Update user's last login (non-blocking)
  const updateLastLogin = async (userEmail) => {
    try {
      console.log('🔄 Updating last login for user:', userEmail);
      
      const { data, error } = await supabase
        .rpc('update_user_last_login', { user_email: userEmail });

      if (error) {
        console.error('❌ Failed to update last login:', error);
      } else if (data?.success) {
        console.log('✅ Last login updated successfully');
      }
    } catch (err) {
      console.error('💥 Error updating last login:', err);
    }
  };

  useEffect(() => {
    let mounted = true;

    // ✅ CRITICAL: Force loading to complete after 1 second maximum
    const forceLoadingTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.log('⏰ Auth loading timeout - forcing completion');
        setLoading(false);
      }
    }, 1000); // Reduced to 1 second

    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('🔄 Getting initial session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('❌ Error getting session:', error);
        } else if (session?.user) {
          console.log('✅ Session found for user:', session.user.email);
          setUser(session.user);
          // Update last login in background (non-blocking)
          updateLastLogin(session.user.email).catch(console.error);
        } else {
          console.log('ℹ️ No active session found');
        }
      } catch (error) {
        console.error('💥 Error in getInitialSession:', error);
      } finally {
        if (mounted) {
          clearTimeout(forceLoadingTimeout);
          setLoading(false); // ✅ ALWAYS stop loading
          console.log('✅ AuthContext loading complete');
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state changed:', event);
        
        if (!mounted) return;
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('✅ User signed in:', session.user.email);
          setUser(prev => (prev?.id === session.user.id ? prev : session.user));

          // Update last login in background (non-blocking)
          updateLastLogin(session.user.email).catch(console.error);

        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // ✅ CRITICAL: Only setUser if id ACTUALLY changed. Supabase
          // returns a fresh user object on every refresh even when id
          // is identical; blindly calling setUser(session.user) created
          // a new object reference → SubscriptionContext re-rendered →
          // fetchProfile fired again → new render → Supabase auto-refresh
          // → 429 rate limit → SIGNED_OUT. This gates the state update.
          console.log('🔄 Token refreshed for:', session.user.email);
          setUser(prev => (prev?.id === session.user.id ? prev : session.user));

        } else if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out');
          setUser(null);
        }
        
        if (mounted) {
          clearTimeout(forceLoadingTimeout);
          setLoading(false); // ✅ ALWAYS stop loading
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(forceLoadingTimeout);
      subscription?.unsubscribe();
    };
  }, []);

  const register = async (firstName, lastName, email, password) => {
    try {
      console.log('🔄 Attempting registration for:', email);
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`.trim()
          }
        }
      });

      if (error) {
        console.error('❌ Registration error:', error);
        throw error;
      }

      if (data.user) {
        console.log('✅ Registration successful for:', data.user.email);
        setUser(data.user);
        
        // Send welcome email via edge function (non-blocking)
        supabase.functions.invoke('send-welcome-email', {
          body: { 
            email: data.user.email, 
            firstName, 
            lastName 
          }
        }).catch(err => console.error('Failed to send welcome email:', err));
      }

      return { data, error: null };
    } catch (error) {
      console.error('💥 Registration error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔄 Attempting login for:', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ Login error:', error);
        throw error;
      }

      if (data.user) {
        console.log('✅ Login successful for:', data.user.email);
        // Update last login in background (non-blocking)
        updateLastLogin(data.user.email).catch(console.error);
      }

      return { data, error: null };
    } catch (error) {
      console.error('💥 Login error:', error);
      return { data: null, error };
    }
  };

  const logout = async () => {
    try {
      console.log('🔄 Starting logout process...');
      
      // Clear user state immediately
      setUser(null);
      
      // Attempt Supabase signout (don't wait for it)
      supabase.auth.signOut().catch(console.error);
      
      // Clear localStorage — project-ref-agnostic so it survives backend migrations.
      try {
        const keysToRemove = ['supabase.auth.token'];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('sb-') && (key.endsWith('-auth-token') || key.endsWith('-auth-token-code-verifier'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
        try { sessionStorage.clear(); } catch (e) {}
      } catch (e) {
        console.warn('Could not clear localStorage:', e);
      }
      
      console.log('✅ Logout completed - redirecting');
      
      // Force redirect
      window.location.href = '/';
      
    } catch (error) {
      console.error('💥 Logout error:', error);
      // Still clear user state and redirect
      setUser(null);
      window.location.href = '/';
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateLastLogin
  };

  console.log('🔐 AuthContext state:', {
    hasUser: !!user,
    userEmail: user?.email,
    loading
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};